/**
 * O que faz: tabela "marco do funil → coluna do Pipeline" com o modo (sugerir ou mover sozinho) de cada marco.
 * Onde usa: CartaoAgenteConversas, dentro da aba Agentes.
 * Conexões: etapas da clínica (useEtapas), regras de retorno e de conversão Meta para bloquear destinos especiais.
 * Tabelas: nenhuma direta; salvar/remover usa as RPCs de rota via useAcoesAgentes.
 * Data: 25/09/2026.
 */
import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MARCOS_FUNIL, TEXTO_MARCO, type CategoriaClassificacao, type ModoRota, type RotaMarco } from '../contratos';

export interface EtapaPipeline {
  id: string;
  nome: string;
  ordem?: number | null;
  eh_etapa_vendas?: boolean | null;
}

interface MapaMarcosColunasProps {
  etapas: EtapaPipeline[];
  rotas: RotaMarco[];
  colunaRetornoId: string | null;
  colunasMetaProtegidas: Set<string>;
  // Quando a clínica autoriza, venda e evento Meta deixam de bloquear o modo automático (retorno continua).
  liberaColunasMeta?: boolean;
  desabilitado: boolean;
  onSalvar: (rota: RotaMarco) => void;
  onRemover: (categoria: CategoriaClassificacao) => void;
}

const SEM_COLUNA = '__sem_coluna__';

export function MapaMarcosColunas(props: MapaMarcosColunasProps) {
  const { etapas, rotas, colunaRetornoId, colunasMetaProtegidas, liberaColunasMeta = false, desabilitado, onSalvar, onRemover } = props;
  const rotaPorMarco = useMemo(() => new Map(rotas.map((rota) => [rota.categoria, rota])), [rotas]);
  const etapasOrdenadas = useMemo(() => [...etapas].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)), [etapas]);

  const motivoProtecao = (etapaId: string): string | null => {
    // Colunas especiais disparam outros fluxos ao receber um card; a IA só pode sugerir para elas.
    const etapa = etapas.find((item) => item.id === etapaId);
    if (colunaRetornoId === etapaId) return 'Coluna de retorno: só sugestão.';
    if (liberaColunasMeta) return null;
    if (etapa?.eh_etapa_vendas) return 'Coluna de venda: só sugestão (libere abaixo, se quiser).';
    if (colunasMetaProtegidas.has(etapaId)) return 'Coluna com evento Meta: só sugestão (libere abaixo, se quiser).';
    return null;
  };

  return (
    <div className="divide-y divide-border rounded-xl border border-border">
      {MARCOS_FUNIL.map((marco) => {
        const texto = TEXTO_MARCO[marco];
        const rota = rotaPorMarco.get(marco);
        const protecao = rota ? motivoProtecao(rota.etapaId) : null;
        const idColuna = `coluna-${marco}`;
        const idModo = `modo-${marco}`;
        return (
          <div key={marco} className="grid gap-3 p-3 sm:grid-cols-[minmax(0,1fr)_200px_170px] sm:items-center">
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">{texto.rotulo}</p>
              <p className="text-xs text-muted-foreground">
                {texto.descricao}{texto.soSistema ? ' Vem dos registros do CRM.' : ''}
              </p>
              {protecao && <p className="mt-1 animate-in fade-in-0 duration-200 motion-reduce:animate-none text-xs text-warning-text">{protecao}</p>}
            </div>
            <div>
              <Label htmlFor={idColuna} className="sr-only">Coluna para {texto.rotulo}</Label>
              <Select
                value={rota?.etapaId ?? SEM_COLUNA}
                disabled={desabilitado}
                onValueChange={(valor) => {
                  if (valor === SEM_COLUNA) { onRemover(marco); return; }
                  // Trocar de coluna volta para sugestão: mover sozinho é uma escolha consciente por coluna.
                  onSalvar({ categoria: marco, etapaId: valor, modo: 'suggestion' });
                }}
              >
                <SelectTrigger id={idColuna}><SelectValue placeholder="Sem coluna" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_COLUNA}>Sem coluna (só classificar)</SelectItem>
                  {etapasOrdenadas.map((etapa) => <SelectItem key={etapa.id} value={etapa.id}>{etapa.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor={idModo} className="sr-only">Modo para {texto.rotulo}</Label>
              <Select
                value={rota?.modo ?? 'suggestion'}
                disabled={desabilitado || !rota}
                onValueChange={(valor) => rota && onSalvar({ ...rota, modo: valor as ModoRota })}
              >
                <SelectTrigger id={idModo}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="suggestion">Só sugerir</SelectItem>
                  <SelectItem value="automatic" disabled={Boolean(protecao)}>Mover sozinho</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );
      })}
    </div>
  );
}
